import { getCookie } from './utils';
import { addAlert } from './alerts';

export function initializeNotifications() {
    const notificationsContainer = document.getElementById(
        'notificationsContainer',
    );
    if (!notificationsContainer) return;
    const notificationsWindow = notificationsContainer.querySelector(
        '#notificationsWindow',
    );
    const toggleButton = notificationsContainer.querySelector(
        'button.window-toggle',
    );
    const notificationsClose =
        notificationsContainer.querySelector('.window-close');
    const notificationsList =
        notificationsWindow.querySelector('.notifications');
    const markAllAsReadButton =
        notificationsWindow.querySelector('.mark-all-as-read');
    const notificationsCounters =
        notificationsContainer.querySelectorAll('.counter');
    const notificationsAllCounters =
        notificationsContainer.querySelectorAll('.all-counter');
    const notificationPing = notificationsContainer.querySelector('.ping');
    let notificationIntervalId = null;

    // --- Pagination State ---
    const PAGE_SIZE = 100; // How many notifications to fetch per request
    let totalUnreadNotifications = 0; // Store unread count from API
    let totalNotifications = 0; // Store total count from API
    let currentOffset = 0;
    let hasMore = true;
    let isLoading = false;

    notificationsList.addEventListener('scroll', () => {
        const threshold = 100;
        const isNearBottom =
            notificationsList.scrollTop + notificationsList.clientHeight >=
            notificationsList.scrollHeight - threshold;

        if (isNearBottom && hasMore && !isLoading) {
            fetchNotifications();
        }
    });

    notificationsClose.addEventListener('click', (event) => {
        event.stopPropagation();
        notificationsContainer.style.pointerEvents = 'none';
        notificationsContainer.classList.add('hidden');
        setTimeout(() => {
            notificationsContainer.style.pointerEvents = 'auto';
            notificationsContainer.classList.remove('hidden');
        }, 10);
    });

    notificationsClose.addEventListener('touchstart', (event) => {
        event.stopPropagation();
        notificationsWindow.classList.remove('open');
        notificationsContainer.style.pointerEvents = 'none';
        setTimeout(() => {
            notificationsContainer.style.pointerEvents = 'auto';
        }, 10);
    });

    markAllAsReadButton.addEventListener('click', async (event) => {
        const url = `/api/notifications/mark_all_as_closed/`;
        let needRestart = false;
        try {
            isLoading = true;
            markAllAsReadButton.disabled = true;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'X-CSRFToken': getCookie('csrftoken'),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            await response.json();
            needRestart = true;
        } catch (error) {
            console.error('Error marking notification as read:', error);
        } finally {
            isLoading = false;
            markAllAsReadButton.disabled = false;
        }
        if (needRestart) {
            startNotificationPolling();
        }
    });

    function renderNotifications(notifications, append = false) {
        if (!append && notifications.length === 0) {
            notificationsList.innerHTML = `<p class="empty">${gettext('Brak powiadomień')}</p>`;
            markAllAsReadButton.classList.add('hidden');
            return;
        }
        markAllAsReadButton.classList.remove('hidden');

        const newHtml = notifications
            .map((notification) => {
                const date = new Date(notification.notification.created_at);
                let content;
                if (notification.notification.request) {
                    content = `<p class="notification-content with_redirect" data-notification-id="${notification.notification.id}">${notification.notification.content}</p>`;
                } else {
                    content = `<p class="notification-content">${notification.notification.content}</p>`;
                }

                return `
                <div class="notification">
                    <div class="notification-wrapper">
                        ${!notification.read ? `<span class="ping"></span>` : ``}
                        <small class="notification-date">${date.toLocaleString()}</small>
                        <h4 class="notification-title">${notification.notification.title}</h4>
                        ${content}
                    </div>
                    <button class="mark-as-read" data-notification-id="${notification.notification.id}">
                        <span class="icon-close-bold"></span>
                    </button>
                </div>
            `;
            })
            .join('');

        if (append) {
            notificationsList.innerHTML = notificationsList.innerHTML + newHtml;
        } else {
            notificationsList.innerHTML = newHtml;
        }

        notificationsList
            .querySelectorAll('.mark-as-read')
            .forEach((button) => {
                button.addEventListener('click', async (event) => {
                    event.preventDefault();
                    await markNotificationAsRead(
                        button,
                        button.dataset.notificationId,
                    );
                });
            });

        notificationsList
            .querySelectorAll('.notification-content')
            .forEach((element) => {
                if (element.dataset.notificationId) {
                    element.addEventListener('click', async (event) => {
                        await markNotificationAsReadWithRedirect(
                            element.dataset.notificationId,
                        );
                    });
                }
            });
    }

    async function updateNotificationCount(count = null) {
        if (count === null) {
            const response = await fetch(`/api/notifications/count`);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();
            count = data;
            totalUnreadNotifications = count;
        } else {
            totalUnreadNotifications = count;
        }
        if (totalNotifications < currentOffset) {
            currentOffset = totalNotifications;
        }
        notificationsCounters.forEach((counter) => {
            counter.textContent = count;
        });
        notificationsAllCounters.forEach((counter) => {
            counter.textContent = `(${totalNotifications})`;
        });
        if (count > 0) {
            notificationPing.classList.remove('hidden');
        } else {
            notificationPing.classList.add('hidden');
        }
    }

    async function fetchNotifications() {
        if (isLoading) return;
        isLoading = true;
        let needRestart = false;

        const url = `/api/notifications/?show_read=true&show_closed=false&limit=${PAGE_SIZE}&offset=${currentOffset}`;

        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            const data = await response.json();

            const append = currentOffset > 0; // Append if not the first page
            currentOffset += data.items.length;
            hasMore = currentOffset < data.count; // Check if more items exist based on total count

            renderNotifications(data.items, append);
            if (totalNotifications !== data.count) {
                totalNotifications = data.count;
                await updateNotificationCount(); // Update count after fetching
                needRestart = true;
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
            notificationsList.innerHTML = '<p>Error loading notifications.</p>';
            hasMore = false;
        } finally {
            isLoading = false;
        }
        if (needRestart) {
            startNotificationPolling();
        }
    }

    async function markNotificationAsRead(markAsReadButton, notificationId) {
        isLoading = true;
        let needRestart = false;

        const url = `/api/notifications/${notificationId}/mark_as_closed/`;
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'X-CSRFToken': getCookie('csrftoken'),
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            }
            await response.json();

            const notificationElement =
                markAsReadButton.closest('.notification');
            if (notificationElement) {
                notificationElement.remove();
                totalNotifications = totalNotifications - 1;
                if (notificationElement.querySelector('.ping')) {
                    await updateNotificationCount(
                        totalUnreadNotifications > 0
                            ? totalUnreadNotifications - 1
                            : 0,
                    );
                } else {
                    await updateNotificationCount(totalUnreadNotifications);
                }
                if (totalNotifications === 0) {
                    needRestart = true;
                }
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        } finally {
            isLoading = false;
        }
        if (needRestart) {
            startNotificationPolling();
        }
    }

    async function markNotificationAsReadWithRedirect(notificationId) {
        const url = `/notifications/mark_as_read/${notificationId}/`;
        try {
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'X-CSRFToken': getCookie('csrftoken'),
                },
            });
            if (!response.ok) {
                throw new Error(`HTTP error! Status: ${response.status}`);
            } else {
                if (response.redirected) {
                    window.location.replace(response.url);
                }
            }
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    }

    function resetPaginationState() {
        currentOffset = 0;
        hasMore = true;
        notificationsList.innerHTML = '';
        notificationsList.scrollTop = 0;
    }

    function resetAndFetchFirstPage() {
        stopNotificationPolling();
        resetPaginationState();
        fetchNotifications();
    }

    function startNotificationPolling() {
        resetAndFetchFirstPage();
        notificationIntervalId = setInterval(fetchNotifications, 15000);
    }

    function stopNotificationPolling() {
        if (notificationIntervalId) {
            clearInterval(notificationIntervalId);
            notificationIntervalId = null;
        }
    }

    if (!toggleButton || !notificationsWindow) return;

    toggleButton.addEventListener('touchstart', (event) => {
        event.stopPropagation();
        notificationsWindow.classList.add('open');
    });

    document.addEventListener('touchstart', (event) => {
        if (
            !toggleButton.contains(event.target) &&
            !notificationsWindow.contains(event.target)
        ) {
            notificationsWindow.classList.remove('open');
        }
    });

    startNotificationPolling();
    return stopNotificationPolling;
}
